import { useRef, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Chip, Popover } from "@narmi/design_system";
import styles from "./RecentTransactionsModule.module.scss";

export interface RangeFilterChipProps {
  label: string;
  valueFrom: string;
  valueTo: string;
  onApply: (from: string, to: string) => void;
  renderFrom: (value: string, onChange: (v: string) => void) => React.ReactNode;
  renderTo: (
    value: string,
    onChange: (v: string) => void,
    hasError: boolean,
    pendingFrom: string
  ) => React.ReactNode;
  validate?: (from: string, to: string) => boolean;
  // Seeds the "to" field the first time the dropdown is opened with no
  // applied "to" value yet (e.g. today's date, so the field isn't blank).
  defaultPendingTo?: string;
}

const RangeFilterChip = ({
  label,
  valueFrom,
  valueTo,
  onApply,
  renderFrom,
  renderTo,
  validate,
  defaultPendingTo,
}: RangeFilterChipProps) => {
  const { l10n } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingFrom, setPendingFrom] = useState(valueFrom);
  const [pendingTo, setPendingTo] = useState(valueTo);
  const contentRef = useRef<HTMLDivElement>(null);

  const hasError = validate ? validate(pendingFrom, pendingTo) : false;

  const closeCalendars = () => {
    contentRef.current
      ?.querySelectorAll<HTMLInputElement>(".flatpickr-input")
      .forEach((el) => {
        // eslint-disable-next-line no-underscore-dangle
        const fp = (el as any)._flatpickr;
        if (fp?.isOpen) fp.close();
      });
  };

  const handleApply = () => {
    if (hasError) return;
    onApply(pendingFrom, pendingTo);
    setIsOpen(false);
  };

  const handleClear = () => {
    // DateFilterInput seeds flatpickr's displayed value once via `defaultDate`
    // (uncontrolled), so resetting pendingFrom/pendingTo alone wouldn't clear
    // what's shown in the field — tell the live flatpickr instance directly.
    contentRef.current
      ?.querySelectorAll<HTMLInputElement>(".flatpickr-input")
      .forEach((el) => {
        // eslint-disable-next-line no-underscore-dangle
        const fp = (el as any)._flatpickr;
        fp?.clear(false);
      });
    setPendingFrom("");
    setPendingTo("");
  };

  return (
    <Popover
      isOpen={isOpen}
      onUserDismiss={() => {
        const cal = document.querySelector(".flatpickr-calendar.open");
        if (cal && cal.matches(":hover")) return;
        closeCalendars();
        setIsOpen(false);
      }}
      content={
        <div ref={contentRef} className={styles.amountPopover}>
          <div className={styles.amountInputRow}>
            {renderFrom(pendingFrom, setPendingFrom)}
            {renderTo(pendingTo, setPendingTo, hasError, pendingFrom)}
          </div>
          <hr className={styles.accountListDividerNoMargin} />
          <div className={styles.searchFooter}>
            <Button kind="plain" size="xs" onClick={handleClear}>
              {l10n.getString("recent-transactions-search-clear")}
            </Button>
            <Button size="xs" onClick={handleApply} disabled={hasError}>
              {l10n.getString("recent-transactions-search-apply")}
            </Button>
          </div>
        </div>
      }
    >
      <Chip
        label={label}
        kind={valueFrom || valueTo ? "primary" : "secondary"}
        endIcon={isOpen ? "chevron-up" : "chevron-down"}
        onClick={() => {
          if (isOpen) {
            closeCalendars();
          } else {
            setPendingFrom(valueFrom);
            setPendingTo(
              !valueTo && defaultPendingTo ? defaultPendingTo : valueTo
            );
          }
          setIsOpen((prev) => !prev);
        }}
      />
    </Popover>
  );
};

export default RangeFilterChip;
